import { Skeleton } from 'components/ui/States'
import { FC, ReactNode } from 'react'

interface IDeltaBadge {
    percentage?: number
    decimals?: number
    label?: string
    /** Shown when there is no previous value to compare with. */
    emptyLabel?: string
}

/** ▲ / ▼ delta pill. `undefined` → 上月無資料, 0 → neutral. */
export const DeltaBadge: FC<IDeltaBadge> = ({ percentage, decimals = 1, label = '較上月', emptyLabel = '上月無資料' }) => {
    if (percentage === undefined || !Number.isFinite(percentage)) {
        return <span className='pill bg-canvas-deep text-ink-muted'>{emptyLabel}</span>
    }
    const text = `${Math.abs(percentage).toFixed(decimals)}%`
    if (percentage === 0) {
        return <span className='inline-flex items-center gap-1.5'>
            <span className='pill bg-canvas-deep font-semibold text-ink-muted tabular-nums'>{text}</span>
            <span>{label}</span>
        </span>
    }
    const positive = percentage > 0
    return <span className='inline-flex items-center gap-1.5'>
        <span
            className={`pill gap-0.5 font-semibold tabular-nums ${positive ? 'bg-success-50 text-success' : 'bg-danger-50 text-danger'}`}
            aria-label={`${positive ? '上升' : '下降'} ${text}`}>
            <span aria-hidden>{positive ? '▲' : '▼'}</span>{text}
        </span>
        <span>{label}</span>
    </span>
}

interface IRecordCardView {
    title: string
    value: ReactNode
    unit?: string
    icon?: ReactNode
    /** Show the delta row (C tiles). */
    showDelta?: boolean
    percentage?: number
    decimals?: number
    deltaLabel?: string
    deltaEmptyLabel?: string
    description?: ReactNode
    loading?: boolean
    /** Smaller value text for long string values (e.g. exercise names). */
    textValue?: boolean
}

/** KPI / stat tile (DESIGN.md §4.4). */
export const RecordCardView: FC<IRecordCardView> = ({ title, value, unit, icon, showDelta, percentage, decimals, deltaLabel, deltaEmptyLabel, description, loading, textValue }) => (
    <div className='card flex min-w-0 flex-col p-4 sm:p-5'>
        <div className='flex items-start justify-between gap-2'>
            <p className='text-sm font-medium text-ink-muted'>{title}</p>
            {icon && <span className='grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary-50 text-primary [&>svg]:h-4 [&>svg]:w-4'>{icon}</span>}
        </div>
        {loading
            ? <div className='mt-3 space-y-2'>
                <Skeleton className='h-8 w-28' />
                <Skeleton className='h-4 w-20' />
            </div>
            : <>
                <p className='mt-3 flex min-w-0 flex-wrap items-baseline gap-x-1'>
                    <span className={`tabular-nums text-ink ${textValue ? 'truncate text-2xl font-semibold tracking-tight' : 'text-2xl font-semibold tracking-tight sm:text-kpi lg:text-kpi-lg'}`}>{value}</span>
                    {unit && <span className='text-sm font-medium text-ink-muted'>{unit}</span>}
                </p>
                {(showDelta || description) && <div className='mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-faint'>
                    {showDelta && <DeltaBadge percentage={percentage} decimals={decimals} label={deltaLabel} emptyLabel={deltaEmptyLabel} />}
                    {description && <span>{description}</span>}
                </div>}
            </>}
    </div>
)
